import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { puntosApi, cardStyle, pillStyle, fmtDateTime } from './puntosApi';
import { Estrella } from '../../publico/Iconos';

const EMPTY_FORM = { mode: 'add', amount: '', reason: '' };

/** Ajuste manual de puntos, por ejemplo para pasar los que el cliente ya tenía de antes. */
const AjustesPuntos = () => {
    const [cedula, setCedula] = useState('');
    const [detail, setDetail] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchCustomer = async (value) => {
        const res = await puntosApi.lookup(value);
        setDetail(res.data);
    };

    const search = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setNotFound(false);
        setForm(EMPTY_FORM);
        try {
            await fetchCustomer(cedula);
        } catch (err) {
            setDetail(null);
            if (err.response?.status === 404) setNotFound(true);
            else setError(err.response?.data?.error || 'No se pudo consultar.');
        }
    };

    // Cambiar la cédula descarta al cliente anterior para no ajustarle los puntos por error
    const changeCedula = (value) => {
        setCedula(value.replace(/\D/g, ''));
        setDetail(null);
        setNotFound(false);
        setError('');
        setSuccess('');
    };

    const amount = parseInt(form.amount, 10) || 0;
    const change = form.mode === 'add' ? amount : -amount;
    const newBalance = detail ? detail.points_balance + change : 0;
    const canSubmit = amount > 0 && form.reason.trim() && newBalance >= 0 && !saving;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await puntosApi.adjustPoints({
                customer_id: detail.customer.id,
                points: change,
                reason: form.reason.trim(),
            });
            setSuccess(`Ajuste guardado. Saldo actual: ${res.data.points_balance} puntos.`);
            setForm(EMPTY_FORM);
            await fetchCustomer(detail.customer.cedula);
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo guardar el ajuste.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
            <div style={cardStyle}>
                <h3 style={{ marginTop: 0, color: 'var(--primary-color)' }}>Ajustar puntos</h3>
                <form onSubmit={search} style={{ display: 'flex', gap: '0.5rem', maxWidth: 480 }}>
                    <input
                        className="form-control"
                        placeholder="Cédula del cliente"
                        inputMode="numeric"
                        value={cedula}
                        onChange={(e) => changeCedula(e.target.value)}
                    />
                    <button className="btn btn-primary" type="submit" disabled={cedula.length < 10}>Buscar</button>
                </form>

                {notFound && (
                    <p style={{ marginBottom: 0, marginTop: '0.75rem' }}>
                        No hay un cliente con esa cédula. Regístralo primero en <Link to="/fast-food/customers">Clientes</Link> y vuelve a buscarlo.
                    </p>
                )}
                {!detail && error && <p style={{ color: '#9b1c1c', marginBottom: 0, marginTop: '0.75rem' }}>{error}</p>}

                {detail && (
                    <form onSubmit={submit} style={{ marginTop: '1.25rem', display: 'grid', gap: '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <div>
                                <strong style={{ color: 'var(--primary-color)' }}>{detail.customer.full_name}</strong>
                                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>C.I. {detail.customer.cedula}</div>
                            </div>
                            <span style={{ ...pillStyle, fontSize: '1.1rem' }}>
                                <Estrella size={13} style={{ verticalAlign: '-2px', color: '#d9a441' }} /> {detail.points_balance}
                            </span>
                        </div>

                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
                            <div>
                                <label style={{ display: 'block' }}>Tipo de ajuste</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button type="button" className={`btn ${form.mode === 'add' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setForm({ ...form, mode: 'add' })}>Sumar</button>
                                    <button type="button" className={`btn ${form.mode === 'subtract' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setForm({ ...form, mode: 'subtract' })}>Restar</button>
                                </div>
                            </div>
                            <div style={{ width: 130 }}>
                                <label htmlFor="ajuste-puntos">Puntos</label>
                                <input
                                    id="ajuste-puntos"
                                    className="form-control"
                                    inputMode="numeric"
                                    maxLength={5}
                                    value={form.amount}
                                    onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/\D/g, '') })}
                                />
                            </div>
                            <div style={{ flex: '1 1 280px' }}>
                                <label htmlFor="ajuste-motivo">Motivo</label>
                                <input
                                    id="ajuste-motivo"
                                    className="form-control"
                                    maxLength={255}
                                    placeholder="Ej.: puntos de la tarjeta física anterior"
                                    value={form.reason}
                                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span style={{ color: newBalance < 0 ? '#9b1c1c' : '#6b7280' }}>
                                {newBalance < 0
                                    ? `No se pueden restar ${amount} puntos: solo tiene ${detail.points_balance}.`
                                    : amount > 0 && <>Saldo después del ajuste: <strong>{newBalance}</strong> puntos</>}
                            </span>
                            <button className="btn btn-primary" type="submit" disabled={!canSubmit} style={{ opacity: canSubmit ? 1 : 0.5 }}>
                                {saving ? 'Guardando…' : 'Guardar ajuste'}
                            </button>
                        </div>

                        {error && <p style={{ color: '#9b1c1c', margin: 0 }}>{error}</p>}
                        {success && <p style={{ color: '#1f6b45', margin: 0, fontWeight: 600 }}>{success}</p>}
                    </form>
                )}
            </div>

            {detail && (
                <div style={cardStyle}>
                    <h3 style={{ marginTop: 0, color: 'var(--primary-color)' }}>Últimos movimientos del cliente</h3>
                    {detail.history.length === 0 ? <p style={{ color: '#6b7280', marginBottom: 0 }}>Todavía no tiene movimientos.</p> : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="table">
                                <tbody>
                                    {detail.history.map((h, i) => (
                                        <tr key={i}>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(h.created_at)}</td>
                                            <td>{h.reason}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: h.points_change >= 0 ? '#1f6b45' : 'var(--primary-color)' }}>
                                                {h.points_change >= 0 ? '+' : ''}{h.points_change}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AjustesPuntos;
